import type { Price } from '~/types'
import { useLogger } from '#layers/base/composables/useLogger'

export interface Campaign {
  name: string
  endDate: string
  discountPercent: number
  discountType: string
  beforePrice: number
  afterPrice: number
  status: string
}

export interface OrderCondition {
  max: number | undefined
  min: number | undefined
  multiplier: number | undefined
  maxQuota: number | undefined
  quotaUnit: string
}

export interface AlternativeProduct {
  fields: Record<string, string>
  idExternal: string
  availability: string
  handle: string
  listPrice: number
  showOrderOptimizationChart: boolean
  references: string[]
  campaign?: Campaign
  isFavourite: boolean
  scalePositions: Array<{ minQuantity: number, price: Price }>
  gpoPrices: Array<{ minQuantity: number, price: Price }>
  individualPrices: Array<{ minQuantity: number, price: Price, reference: string }>
  imageUrl?: string
  manufacturer?: { name: string, handle: string }
  bestBeforeDate?: string | null
  lastOrderedQuantity?: number
  lastOrderedDate?: string
  preselectedReference?: string
  orderCondition?: OrderCondition
  isSubscribed: boolean
  clinicChain?: { handle: string, name: string, color?: string, group?: string }
}

export interface AlternativeSupplier {
  handle: string
  name?: string
  items: AlternativeProduct[]
}

export interface ProductGroup {
  name: string
  suppliers: AlternativeSupplier[]
}

export interface ProductAlternatives {
  matchingCriteria: string
  productGroups: ProductGroup[]
}

export function filterAlternatives(
  data: ProductAlternatives[],
  {
    excludedSuppliers,
    excludedGroupName,
  }: {
    excludedSuppliers?: Set<string>
    excludedGroupName?: string
  },
): ProductAlternatives[] {
  const hasSupplierExclusions = !!excludedSuppliers?.size
  if (!hasSupplierExclusions && !excludedGroupName) return data

  return data
    .map(cluster => ({
      ...cluster,
      productGroups: cluster.productGroups
        .filter(group => !excludedGroupName || group.name !== excludedGroupName)
        .map((group) => {
          if (!hasSupplierExclusions) return group
          return {
            ...group,
            suppliers: group.suppliers.filter(s => !excludedSuppliers!.has(s.handle)),
          }
        })
        .filter(group => !hasSupplierExclusions || group.suppliers.length > 0),
    }))
    .filter(cluster => cluster.productGroups.length > 0)
}

// Singleton store: assumes only one "explore alternatives" flow is active at a time.
// Both the cart sheet (scope 'gpo') and the global overlay sheet in layouts/default.vue
// (scope 'all') share this instance, so concurrent fetches would clobber each other.
export const useProductAlternativesStore = defineStore('product-alternatives', () => {
  const { baseURL } = useRuntimeConfig().app
  const log = useLogger('productAlternativesStore')
  const userStore = useUserStore()
  const featureSettingsStore = useFeatureSettingsStore()

  const productHandle = ref<string>('none')
  const scope = ref<'gpo' | 'all'>('all')
  const sourceGroupName = ref<string | undefined>()
  const customerHandle = computed(() => userStore.customerHandle)
  const accessToken = computed(() => userStore.accessToken)
  const excludedSuppliers = computed(() => {
    const clinicChainHandle = unref(userStore.gpoHandle) ?? 'none'
    return new Set(featureSettingsStore.settings?.clinicChain?.[clinicChainHandle]?.exclude?.suppliers ?? [])
  })

  const headerFields = computed(() => ({ Authorization: `Bearer ${unref(accessToken)}` }))
  const query = computed(() => ({ cHandle: unref(customerHandle), scope: unref(scope) }))
  const alternativesUrl = computed(() => {
    const handle = unref(productHandle)
    return handle ? `/api/intern/products/${handle}/alternatives` : ''
  })

  const {
    data: alternativesData,
    pending: isLoading,
    refresh: refreshAlternatives,
    error: alternativesError,
  } = useFetch<ProductAlternatives[]>(alternativesUrl, {
    baseURL,
    headers: headerFields,
    query,
    watch: [accessToken],
    immediate: false,
    onRequest({ options }) {
      if (!unref(accessToken)) {
        options.signal = AbortSignal.abort()
        return
      }
      const handle = unref(productHandle)
      if (handle === 'none' || !handle) {
        log.info('Skip product alternatives request, missing product handle')
        options.signal = AbortSignal.abort()
      }
    },
    onResponseError({ response }) {
      log.warn('Failed to fetch product alternatives', {
        productHandle: unref(productHandle),
        responseStatus: response.status,
      })
      // Fire-and-forget: refreshing the token flips `accessToken`, and the
      // `watch: [accessToken]` above performs the single recovery refetch.
      if (response.status === 401) {
        void userStore.reloadAuth()
      }
    },
    onResponse({ response }) {
      log.info('Product alternatives fetched successfully', {
        productHandle: unref(productHandle),
        alternativesCount: response?._data?.length ?? 0,
      })
    },
  })

  // Apply GPO supplier blocks and optionally drop the source product group.
  const alternatives = computed(() => {
    if (!alternativesData.value) return null
    return filterAlternatives(alternativesData.value, {
      excludedSuppliers: excludedSuppliers.value,
      excludedGroupName: sourceGroupName.value,
    })
  })

  async function fetchAlternatives(
    handle: string,
    requestedScope: 'gpo' | 'all' = 'all',
    excludedGroupName?: string,
  ) {
    if (!handle) {
      log.warn('Cannot fetch alternatives without product handle')
      return
    }

    productHandle.value = handle
    scope.value = requestedScope
    sourceGroupName.value = excludedGroupName
    await refreshAlternatives()
  }

  function $reset() {
    productHandle.value = 'none'
    sourceGroupName.value = undefined
    alternativesData.value = null
  }

  return {
    productHandle,
    alternatives,
    isLoading,
    alternativesError,
    fetchAlternatives,
    refreshAlternatives,
    $reset,
  }
})
